import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getAllZipsString, getAllNeighborhoodsString } from '../data/nycZipCodes';

/**
 * Comprehensive SEO Component
 * Optimized for: Google, Bing, AI Search, Social Media (FB, IG, TikTok, X, YouTube), Voice Search
 * Coverage: All NYC zip codes and neighborhoods
 */
const ComprehensiveSEO = () => {
  const businessName = 'NY Pest & Exterminating';
  const businessPhone = '(646) 209-9955';
  const businessPhoneRaw = '+16462099955';
  const baseUrl = 'https://nypestexterminating.com';
  const businessImage = 'https://customer-assets.emergentagent.com/job_ecopestcontrol/artifacts/7osc181x_WhyProfessionalPestControlServicesareVitalforHomes.jpg';

  // Comprehensive ranking keywords
  const rankingKeywords = [
    // Primary service keywords
    'pest control NYC', 'exterminator NYC', 'pest control New York',
    'bed bug treatment NYC', 'rodent control NYC', 'termite inspection NYC',
    'cockroach exterminator NYC', 'ant control NYC', 'mosquito control NYC',
    
    // Location-based keywords (near me)
    'pest control near me', 'exterminator near me', 'bed bugs near me',
    'rodent control near me', 'termite inspection near me',
    
    // Service type keywords
    'eco friendly pest control', 'organic pest control', 'green pest control',
    'pet safe pest control', 'child safe exterminator', 'non toxic pest control',
    'residential pest control', 'commercial pest control', 'apartment pest control',
    
    // Urgency keywords
    '24 hour pest control', 'emergency pest control', 'same day pest control',
    'pest control open now', 'weekend pest control', 'immediate pest control',
    
    // Borough keywords
    'pest control Brooklyn', 'pest control Manhattan', 'pest control Queens',
    'pest control Bronx', 'pest control Staten Island',
    'exterminator Brooklyn', 'exterminator Manhattan', 'exterminator Queens',
    'exterminator Bronx', 'exterminator Staten Island',
    
    // Specific pest keywords
    'bed bug exterminator', 'rat exterminator', 'mice removal', 'cockroach removal',
    'termite treatment', 'flea treatment', 'wasp removal', 'spider control',
    'ant exterminator', 'mosquito treatment', 'fly control',
    
    // Long-tail voice search keywords
    'how to get rid of bed bugs NYC', 'best pest control company NYC',
    'affordable pest control NYC', 'professional pest control NYC',
    'licensed pest control NYC', 'certified exterminator NYC',
    
    // Seasonal keywords
    'winter pest control', 'summer pest control', 'spring pest control', 'fall pest control',
    'rodent control winter', 'mosquito control summer', 'ant control spring',
    
    // Property type keywords
    'restaurant pest control', 'hotel pest control', 'office pest control',
    'warehouse pest control', 'retail pest control', 'school pest control',
    'hospital pest control', 'food service pest control'
  ];

  // Social Media specific keywords for algorithm optimization
  const socialMediaKeywords = [
    '#PestControl', '#NYC', '#NewYork', '#Exterminator', '#EcoFriendly',
    '#BedBugs', '#RodentControl', '#PestFree', '#HomeService', '#LocalBusiness',
    '#NYCBusiness', '#Brooklyn', '#Manhattan', '#Queens', '#Bronx', '#StatenIsland',
    '#PestPrevention', '#HealthyHome', '#SafeHome', '#GreenCleaning', '#OrganicPestControl',
    '#PetSafe', '#FamilySafe', '#EmergencyService', '#24HourService', '#SameDayService'
  ];

  // AI Search optimized description
  const aiOptimizedDescription = `${businessName} provides professional eco-friendly pest control services across all NYC boroughs and zip codes. Available 24/7 for emergency pest control with same-day service. Specializing in bed bug heat treatment, rodent exclusion, termite inspection, cockroach elimination, and commercial pest management. Licensed, insured, and Green Shield certified. Serving ${getAllNeighborhoodsString().split(',').slice(0, 20).join(', ')} and all 200+ NYC neighborhoods.`;

  // Voice search optimized structured data
  const voiceSearchSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who is the best pest control company in NYC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${businessName} is NYC's top-rated eco-friendly pest control service with 4.9 stars. We serve all NYC zip codes including ${getAllZipsString().split(',').slice(0, 15).join(', ')} and more. Available 24/7. Call ${businessPhone}.`
        }
      },
      {
        "@type": "Question",
        "name": "How much does pest control cost in NYC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Pest control in NYC typically ranges from $150-$500 depending on service type and property size. ${businessName} offers free quotes. Call ${businessPhone} for accurate pricing.`
        }
      },
      {
        "@type": "Question",
        "name": "Is there 24 hour pest control near me in NYC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Yes! ${businessName} offers 24/7 emergency pest control throughout all NYC zip codes with same-day service. Call ${businessPhone} anytime for immediate help.`
        }
      },
      {
        "@type": "Question",
        "name": "What areas does NY Pest & Exterminating serve?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `We serve all 5 NYC boroughs and 200+ neighborhoods including Manhattan, Brooklyn, Queens, Bronx, and Staten Island. Coverage includes all zip codes from 10001 to 11697.`
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer eco-friendly pest control?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Yes! ${businessName} specializes in Green Shield certified eco-friendly pest control that's safe for children, pets, and the environment. Call ${businessPhone}.`
        }
      }
    ]
  };

  // Enhanced service schema for AI search
  const enhancedServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${baseUrl}/#professionalservice`,
    "name": businessName,
    "telephone": businessPhoneRaw,
    "email": "info@nypestexterminating.com",
    "url": baseUrl,
    "priceRange": "$$",
    "serviceType": [
      "Bed Bug Treatment", "Rodent Control", "Termite Inspection",
      "Cockroach Extermination", "Ant Control", "Mosquito Treatment",
      "Flea Treatment", "Wasp Removal", "Spider Control",
      "Commercial Pest Control", "Residential Pest Control"
    ],
    "areaServed": [
      "Manhattan, NY", "Brooklyn, NY", "Queens, NY", "Bronx, NY", "Staten Island, NY",
      "New York City", "NYC Metro Area", "All NYC Zip Codes"
    ],
    "availableLanguage": ["English", "Spanish"],
    "slogan": "NYC's Trusted Eco-Friendly Pest Control Experts - 24/7 Service",
    "knowsAbout": [
      "Eco-Friendly Pest Control", "Bed Bug Heat Treatment", "Rodent Exclusion",
      "Termite Prevention", "Integrated Pest Management", "Green Shield Certification",
      "Commercial Pest Management", "Residential Pest Solutions"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Pest Control Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Emergency Bed Bug Treatment",
            "description": "24/7 same-day bed bug heat treatment and inspection",
            "serviceType": "Pest Control"
          },
          "availability": "https://schema.org/InStock",
          "availabilityStarts": "00:00",
          "availabilityEnds": "23:59"
        }
      ]
    }
  };

  return (
    <Helmet>
      {/* Primary Title & Description for Google/Bing */}
      <meta name="description" content={aiOptimizedDescription} />
      
      {/* Comprehensive Keywords for Ranking */}
      <meta name="keywords" content={rankingKeywords.join(', ')} />
      
      {/* AI Search Optimization */}
      <meta name="AI-search-optimized" content="true" />
      <meta name="chatbot-friendly" content="true" />
      <meta name="AI-description" content={aiOptimizedDescription} />
      
      {/* Location Coverage */}
      <meta name="service-areas" content="Manhattan, Brooklyn, Queens, Bronx, Staten Island, NYC" />
      <meta name="zip-codes-served" content={getAllZipsString()} />
      <meta name="neighborhoods-served" content={getAllNeighborhoodsString()} />
      
      {/* Voice Search Optimization */}
      <meta name="voice-search-optimized" content="true" />
      <meta name="speakable" content="true" />
      <meta name="assistant-friendly" content="Alexa, Google Assistant, Siri" />
      
      {/* Social Media Platform Tags */}
      
      {/* Facebook/Meta Optimization */}
      <meta property="fb:app_id" content="nypestexterminating" />
      <meta property="og:type" content="business.business" />
      <meta property="og:title" content={`${businessName} - 24/7 Eco-Friendly Pest Control in All NYC Zip Codes`} />
      <meta property="og:description" content={aiOptimizedDescription} />
      <meta property="og:url" content={baseUrl} />
      <meta property="og:image" content={businessImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Professional eco-friendly pest control service in NYC" />
      <meta property="og:site_name" content={businessName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:phone_number" content={businessPhoneRaw} />
      <meta property="business:contact_data:street_address" content="New York City" />
      <meta property="business:contact_data:locality" content="New York" />
      <meta property="business:contact_data:region" content="NY" />
      <meta property="business:contact_data:postal_code" content="Multiple NYC Locations" />
      <meta property="business:contact_data:phone_number" content={businessPhoneRaw} />
      
      {/* Instagram Optimization */}
      <meta property="instapp:owner_user_id" content="nypestexterminating" />
      <meta property="og:see_also" content="https://www.instagram.com/nypestexterminating" />
      
      {/* TikTok Optimization */}
      <meta name="tiktok:app:id" content="nypestexterminating" />
      <meta property="og:video" content={`${baseUrl}/videos/service-demo`} />
      <meta property="og:video:type" content="video/mp4" />
      
      {/* Twitter/X Optimization */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@nypestcontrol" />
      <meta name="twitter:creator" content="@nypestcontrol" />
      <meta name="twitter:title" content={`${businessName} - NYC Pest Control in All Zip Codes`} />
      <meta name="twitter:description" content={aiOptimizedDescription} />
      <meta name="twitter:image" content={businessImage} />
      <meta name="twitter:image:alt" content="Professional pest control service" />
      
      {/* YouTube Optimization */}
      <meta property="og:video:url" content="https://www.youtube.com/@nypestexterminating" />
      <meta name="youtube:channel" content="nypestexterminating" />
      
      {/* Enhanced Mobile/App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content="NY Pest Control" />
      
      {/* Social Media Keywords/Hashtags */}
      <meta name="hashtags" content={socialMediaKeywords.join(', ')} />
      
      {/* Schema.org Structured Data for Voice & AI Search */}
      <script type="application/ld+json">
        {JSON.stringify(voiceSearchSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(enhancedServiceSchema)}
      </script>
    </Helmet>
  );
};

export default ComprehensiveSEO;
